import { useState, useEffect, useCallback, useMemo } from "react";
import { VOCAB } from "../data/vocab";
import { recallProbability, updateHalfLife, selectWordFromPool, getXP, getStatus } from "../hooks/useHLR";
import { getSentence } from "../data/sentences";
import { useIsMobile } from "../hooks/useIsMobile";

const QUESTIONS = 20;

function pickDistractors(correctId) {
  const others = VOCAB.filter(v => v.id !== correctId);
  const shuffled = [...others].sort(() => Math.random() - 0.5);
  return shuffled.slice(0, 3);
}

export default function PracticeScreen({ mission, wordStates, onAnswer, onExit, streak, sessionXP, answeredCount, startOffset = 0 }) {
  const isMobile = useIsMobile();
  const missionWordStates = mission.wordIds.map(id => wordStates[id] || { id, correctCount: 0, incorrectCount: 0, lastReviewTime: 0, halfLife: 1.0 });
  const [currentWS, setCurrentWS] = useState(() => {
    if (mission.forcedFirstId) {
      const forced = missionWordStates.find(w => w.id === mission.forcedFirstId);
      if (forced) return forced;
    }
    return selectWordFromPool(missionWordStates);
  });
  const [flipped, setFlipped] = useState(false);
  const [selected, setSelected] = useState(null);
  const [feedback, setFeedback] = useState(null);
  const [showNext, setShowNext] = useState(false);

  const vocab = VOCAB.find(v => v.id === currentWS?.id);

  const options = useMemo(() => {
    if (!vocab) return [];
    const distractors = pickDistractors(vocab.id);
    const all = [
      { id: vocab.id, text: vocab.translation, correct: true },
      ...distractors.map(d => ({ id: d.id, text: d.translation, correct: false })),
    ];
    return all.sort(() => Math.random() - 0.5);
  }, [vocab?.id]);

  useEffect(() => {
    setFlipped(false); setSelected(null); setFeedback(null); setShowNext(false);
  }, [currentWS?.id]);

  const handleSelect = useCallback((opt) => {
    if (selected !== null) return;
    setSelected(opt.id);
    setFeedback(opt.correct ? "correct" : "incorrect");
    // Flip card after a short delay
    setTimeout(() => setFlipped(true), 500);
    // Show Next button only AFTER flip animation completes (700ms flip + 500ms delay + 300ms pause to read)
    setTimeout(() => setShowNext(true), 1800);
  }, [selected]);

  const handleNext = useCallback(() => {
    if (!feedback) return;
    const correct = feedback === "correct";
    const newHL = updateHalfLife(currentWS, correct);
    const xpEarned = getXP(correct, streak, currentWS.halfLife);
    onAnswer(currentWS.id, correct, newHL, xpEarned);

    const isLast = startOffset + answeredCount + 1 >= QUESTIONS;
    if (!isLast) {
      const updatedPool = mission.wordIds.map(id =>
        id === currentWS.id
          ? { ...currentWS, halfLife: newHL, correctCount: currentWS.correctCount + (correct ? 1 : 0), incorrectCount: currentWS.incorrectCount + (correct ? 0 : 1), lastReviewTime: Date.now() / 3600000 }
          : (wordStates[id] || { id, correctCount: 0, incorrectCount: 0, lastReviewTime: 0, halfLife: 1.0 })
      );
      setCurrentWS(selectWordFromPool(updatedPool));
    }
  }, [feedback, currentWS, streak, onAnswer, mission, wordStates, startOffset, answeredCount]);

  if (!vocab) return null;

  const sentence = getSentence(vocab.id, vocab);
  const displayQuestion = startOffset + answeredCount + 1;
  const sessionPct = Math.min(100, ((displayQuestion - 1) / QUESTIONS) * 100);

  return (
    <div style={{ maxWidth: 640, margin: "0 auto" }}>
      {/* Top bar */}
      <div style={{ background: "#fff", borderRadius: "18px 18px 0 0", padding: "0 24px", height: 58, display: "flex", alignItems: "center", justifyContent: "space-between", border: "1px solid #ede8df", borderBottom: "none" }}>
        <button onClick={onExit} style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 13, fontWeight: 600, color: "#b0a898", cursor: "pointer", background: "none", border: "none", fontFamily: "inherit" }}>
          <svg width="14" height="14" viewBox="0 0 15 15" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round"><path d="M9.5 3L5 7.5l4.5 4.5"/></svg>
          Exit
        </button>
        <div style={{ fontSize: 13, fontWeight: 700, color: "#111" }}>{mission.title}</div>
        <div style={{ display: "flex", alignItems: "center", gap: 6, background: "#e8f7f1", border: "1.5px solid #4ec99a", borderRadius: 9, padding: "5px 11px", fontSize: 12, fontWeight: 700, color: "#0a6644" }}>
          <svg width="12" height="12" viewBox="0 0 13 13" fill="none" stroke="#1a8f68" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"><polygon points="6.5,1 8.2,4.8 12.5,5.2 9.3,8.2 10.3,12.5 6.5,10.5 2.7,12.5 3.7,8.2 0.5,5.2 4.8,4.8"/></svg>
          +{sessionXP} XP
        </div>
      </div>

      <div style={{ height: 6, background: "#ede8df", border: "1px solid #ede8df", borderTop: "none", borderBottom: "none" }}>
        <div style={{ height: "100%", background: "#1a8f68", width: `${sessionPct}%`, transition: "width 0.4s ease" }} />
      </div>

      <div style={{ background: "#fff", borderRadius: "0 0 18px 18px", padding: isMobile ? "14px 12px" : "20px 28px 20px", border: "1px solid #ede8df", borderTop: "none" }}>
        <div style={{ fontSize: 11, fontWeight: 700, color: "#b0a898", letterSpacing: 1, textTransform: "uppercase", textAlign: "center", marginBottom: 6 }}>
          Question {displayQuestion} of {QUESTIONS}
        </div>

        {/* Flashcard */}
        <div style={{ perspective: "1200px", marginBottom: 14 }}>
          <div style={{
            position: "relative", width: "100%", height: isMobile ? 170 : 210,
            transformStyle: "preserve-3d",
            transition: "transform 0.7s cubic-bezier(0.4, 0.0, 0.2, 1)",
            transform: flipped ? "rotateY(180deg)" : "rotateY(0deg)",
          }}>
            {/* FRONT */}
            <div style={{
              position: "absolute", inset: 0, backfaceVisibility: "hidden",
              background: "#f5f0e8", borderRadius: 16, padding: "20px 24px",
              border: "1.5px solid #e8e2d8",
              display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", gap: 8,
            }}>
              <div style={{ fontSize: 11, fontWeight: 700, color: "#b0a898", letterSpacing: 1.5, textTransform: "uppercase" }}>
                Translate this word
              </div>
              <div style={{ fontSize: 52, fontWeight: 900, color: "#111", direction: "rtl", fontFamily: "Vazirmatn, 'Scheherazade New', Georgia, serif", lineHeight: 1.25, letterSpacing: -1 }}>
                {vocab.word}
              </div>
              <div style={{ display: "inline-block", background: "#e8f7f1", color: "#0a6644", fontSize: 10, fontWeight: 700, padding: "3px 10px", borderRadius: 20, letterSpacing: 0.5 }}>
                Word #{vocab.id}
              </div>
            </div>

            {/* BACK */}
            <div style={{
              position: "absolute", inset: 0, backfaceVisibility: "hidden",
              transform: "rotateY(180deg)",
              background: feedback === "correct" ? "#e8f7f1" : "#fef2f2",
              borderRadius: 16, padding: "16px 20px",
              border: `2px solid ${feedback === "correct" ? "#1a8f68" : "#e24b4a"}`,
              display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", gap: 6,
            }}>
              <div style={{ width: 34, height: 34, borderRadius: "50%", background: feedback === "correct" ? "#1a8f68" : "#e24b4a", display: "flex", alignItems: "center", justifyContent: "center" }}>
                {feedback === "correct" ? (
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="3" strokeLinecap="round"><path d="M5 12l5 5L20 7"/></svg>
                ) : (
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="3" strokeLinecap="round"><path d="M6 6l12 12M18 6L6 18"/></svg>
                )}
              </div>
              <div style={{ fontSize: 28, fontWeight: 900, color: "#111", direction: "rtl", fontFamily: "Vazirmatn, 'Scheherazade New', Georgia, serif", lineHeight: 1.2 }}>{vocab.word}</div>
              <div style={{ fontSize: 15, fontWeight: 700, color: feedback === "correct" ? "#0a6644" : "#a32d2d" }}>{vocab.translation}</div>
              <div style={{ background: "#fff", borderRadius: 10, padding: "8px 14px", textAlign: "center", border: "1px solid #ede8df", maxWidth: "95%" }}>
                <div style={{ fontSize: 10, fontWeight: 700, color: "#b0a898", letterSpacing: 1, textTransform: "uppercase", marginBottom: 3 }}>Example</div>
                <div style={{ fontSize: 16, fontWeight: 700, color: "#111", direction: "rtl", fontFamily: "Vazirmatn, 'Scheherazade New', Georgia, serif", marginBottom: 3, lineHeight: 1.3 }}>{sentence.farsi}</div>
                <div style={{ fontSize: 11, color: "#888", fontStyle: "italic" }}>{sentence.english}</div>
              </div>
            </div>
          </div>
        </div>

        {/* Options or Next */}
        {!showNext ? (
          <>
            <div style={{ fontSize: 13, fontWeight: 700, color: "#111", textAlign: "center", marginBottom: 12 }}>
              {selected === null ? "What does this mean in English?" : "Read the example sentence…"}
            </div>
            <div style={{ display: "grid", gridTemplateColumns: isMobile ? "1fr" : "1fr 1fr", gap: 8 }}>
              {options.map((opt, i) => {
                const letter = ["A", "B", "C", "D"][i];
                const isSelected = selected === opt.id;
                const showCorrect = selected !== null && opt.correct;
                const showWrong = isSelected && !opt.correct;
                let bg = "#faf8f5", border = "#e8e2d8", color = "#333", letterBg = "#e8e2d8", letterColor = "#888";
                if (showCorrect) { bg = "#e8f7f1"; border = "#1a8f68"; color = "#0a6644"; letterBg = "#1a8f68"; letterColor = "#fff"; }
                else if (showWrong) { bg = "#fef2f2"; border = "#e24b4a"; color = "#a32d2d"; letterBg = "#e24b4a"; letterColor = "#fff"; }
                else if (selected !== null) { bg = "#faf8f5"; border = "#e8e2d8"; color = "#bbb"; }
                return (
                  <button key={opt.id} onClick={() => handleSelect(opt)} disabled={selected !== null} style={{
                    background: bg, border: `2px solid ${border}`, borderRadius: 12, padding: "12px 14px",
                    fontSize: 13, fontWeight: 600, color, cursor: selected !== null ? "default" : "pointer",
                    textAlign: "left", display: "flex", alignItems: "center", gap: 9, fontFamily: "inherit",
                    transition: "all 0.15s",
                  }}>
                    <div style={{ width: 24, height: 24, borderRadius: 7, background: letterBg, color: letterColor, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 11, fontWeight: 800, flexShrink: 0 }}>{letter}</div>
                    {opt.text}
                  </button>
                );
              })}
            </div>
          </>
        ) : (
          <button onClick={handleNext} style={{
            width: "100%", padding: "14px", background: feedback === "correct" ? "#1a8f68" : "#3b82f6",
            color: "#fff", border: "none", borderRadius: 13, fontSize: 15, fontWeight: 800,
            cursor: "pointer", fontFamily: "inherit", display: "flex", alignItems: "center", justifyContent: "center", gap: 8,
            animation: "popIn 0.4s ease",
          }}>
            {displayQuestion >= QUESTIONS ? "Complete mission" : "Next question"}
            <svg width="14" height="14" viewBox="0 0 16 16" fill="none" stroke="white" strokeWidth="2.2" strokeLinecap="round"><path d="M3 8h10M9 4l4 4-4 4"/></svg>
          </button>
        )}
      </div>
    </div>
  );
}
